import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { US_STATES, CITIES_BY_STATE } from "@/lib/usGeo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, MapPin, Building2, Map as MapIcon, Check, X } from "lucide-react";

const KIND_META = {
  zip: { label: "ZIP code", icon: MapPin },
  city: { label: "City", icon: Building2 },
  state: { label: "State", icon: MapIcon },
};

export default function Territory() {
  const { user } = useAuth();
  const canAssign = user.role === "admin" || user.role === "manager";
  const [zones, setZones] = useState([]);
  const [team, setTeam] = useState([]);
  const [kind, setKind] = useState("zip");
  const [zip, setZip] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [cities, setCities] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [assignee, setAssignee] = useState(user.user_id);

  const load = () => api.get("/territories").then(({ data }) => setZones(data)).catch(() => {});
  useEffect(() => { load(); api.get("/team").then(({ data }) => setTeam(data)).catch(() => {}); }, []);

  const toggleCity = (c) => setCities((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);

  const claim = async () => {
    const payload = { kind, user_id: canAssign ? assignee : undefined };
    if (kind === "zip") {
      if (!zip.trim()) return toast.error("Enter a ZIP code");
      payload.zip = zip.trim();
    } else if (kind === "state") {
      if (!stateCode) return toast.error("Choose a state");
      payload.state = stateCode;
    } else {
      if (!stateCode) return toast.error("Choose a state");
      if (!cities.length) return toast.error("Pick at least one city");
      payload.state = stateCode; payload.cities = cities;
    }
    try {
      const { data } = await api.post("/territories", payload);
      toast.success(`Claimed ${data.created.length} zone${data.created.length !== 1 ? "s" : ""}`);
      setZip(""); setCities([]);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const release = async (z) => {
    try { await api.delete(`/territories/${z.territory_id}`); toast.success(`Released ${z.label}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const availableCities = stateCode ? (CITIES_BY_STATE[stateCode] || []) : [];
  const cq = citySearch.trim().toLowerCase();
  const filteredCities = cq ? availableCities.filter((c) => c.toLowerCase().includes(cq)) : availableCities;
  const suggestionMatch = !cq || availableCities.some((c) => c.toLowerCase() === cq) || cities.some((c) => c.toLowerCase() === cq);
  const addCity = (raw) => {
    const c = (raw || "").trim();
    if (!c) return;
    setCities((p) => p.some((x) => x.toLowerCase() === c.toLowerCase()) ? p : [...p, c]);
    setCitySearch("");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Territory zones</h1>
        <p className="mt-1 text-sm text-muted-foreground">Claim ZIP codes, whole states, or specific cities so the Area Finder can auto-suggest the right owner.</p>
      </div>

      <Card className="p-5">
        <div className="grid gap-4 md:grid-cols-[auto_1fr_1fr_auto] md:items-end">
          <div>
            <Label>Claim by</Label>
            <Select value={kind} onValueChange={(v) => { setKind(v); setCities([]); }}>
              <SelectTrigger className="mt-1.5 w-[140px]" data-testid="territory-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="zip">ZIP code</SelectItem>
                <SelectItem value="city">City</SelectItem>
                <SelectItem value="state">State</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {kind === "zip" && (
            <div className="md:col-span-2">
              <Label>ZIP code</Label>
              <Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="e.g. 90012" className="mt-1.5" data-testid="territory-zip-input" />
            </div>
          )}
          {(kind === "state" || kind === "city") && (
            <div className={kind === "state" ? "md:col-span-2" : ""}>
              <Label>State</Label>
              <Select value={stateCode} onValueChange={(v) => { setStateCode(v); setCities([]); }}>
                <SelectTrigger className="mt-1.5" data-testid="territory-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {US_STATES.map(([code, nm]) => <SelectItem key={code} value={code}>{nm}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {kind === "city" && (
            <div>
              <Label>Cities {cities.length > 0 && <span className="text-primary">({cities.length})</span>}</Label>
              {!stateCode ? (
                <p className="mt-1.5 text-xs text-muted-foreground">Pick a state first.</p>
              ) : (
                <div className="mt-1.5 space-y-2">
                  {cities.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="territory-selected-cities">
                      {cities.map((c) => (
                        <span key={c} data-testid={`territory-selected-${c}`}
                          className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/15 px-2.5 py-1 text-xs text-primary">
                          {c}<button type="button" onClick={() => toggleCity(c)} data-testid={`territory-remove-${c}`}><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <Input value={citySearch} onChange={(e) => setCitySearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCity(citySearch); } }}
                    placeholder="Search or type any city, press Enter to add" data-testid="territory-city-search" />
                  <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-border p-2" data-testid="territory-cities">
                    {filteredCities.map((c) => {
                      const active = cities.includes(c);
                      return (
                        <button key={c} type="button" onClick={() => toggleCity(c)} data-testid={`territory-city-${c}`}
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${active ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>
                          {active && <Check className="h-3 w-3" />} {c}
                        </button>
                      );
                    })}
                    {cq && !suggestionMatch && (
                      <button type="button" onClick={() => addCity(citySearch)} data-testid="territory-city-add-custom"
                        className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary/60 px-2.5 py-1 text-xs text-primary hover:bg-primary/10">
                        <Plus className="h-3 w-3" /> Add “{citySearch.trim()}”
                      </button>
                    )}
                    {filteredCities.length === 0 && !cq && <span className="text-xs text-muted-foreground">Type any city name to add it.</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          <Button onClick={claim} data-testid="territory-claim-button"><Plus className="mr-2 h-4 w-4" /> Claim</Button>
        </div>
        {canAssign ? (
          <div className="mt-4 max-w-xs">
            <Label>Assign to</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger className="mt-1.5" data-testid="territory-assignee-select"><SelectValue /></SelectTrigger>
              <SelectContent>{team.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name} ({m.role})</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : <p className="mt-3 text-sm text-muted-foreground">Zones you claim are assigned to you.</p>}
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="territory-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Zone</th>
              <th className="px-4 py-3 font-medium">Owner</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {zones.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground" data-testid="territory-empty">No zones claimed yet.</td></tr>
            ) : zones.map((z) => {
              const meta = KIND_META[z.kind] || KIND_META.zip;
              return (
                <tr key={z.territory_id} className="border-b border-border/60" data-testid={`territory-row-${z.territory_id}`}>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-muted-foreground"><meta.icon className="h-3.5 w-3.5" /> {meta.label}</span></td>
                  <td className="px-4 py-3 font-mono">{z.label}</td>
                  <td className="px-4 py-3">{z.agent_name || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{z.agent_role || "—"}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    {(canAssign || z.user_id === user.user_id) && (
                      <Button size="icon" variant="ghost" onClick={() => release(z)} data-testid={`territory-release-${z.territory_id}`} className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
